import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Icon from '../ui/Icon';
import spaceApi, { SPACES_CHANGED_EVENT } from '../spaceApi';
import { getSpaceId, setActiveSpace, activeSpaceId, clearActiveSpace } from '../getSpaceId';
import './shell.css';

/**
 * Always-visible indicator of the active workspace. `variant`:
 *   'sidebar' — a pill at the top of the desktop sidebar
 *   'strip'   — a slim full-width bar on mobile shell screens
 *
 * Tapping it opens a dropdown to switch workspaces inline, plus a link to the
 * full Workspaces page for managing members / creating / renaming.
 */
export default function WorkspaceIndicator({ userInfo, variant = 'sidebar' }) {
  const history = useHistory();
  const [spaces, setSpaces] = useState([]);
  const [open, setOpen] = useState(false);
  const [, bump] = useState(0);
  const wrapRef = useRef(null);
  const active = getSpaceId(userInfo);

  useEffect(() => {
    let alive = true;
    // Apply a fetched list and self-heal a dangling active pointer: if the
    // active space no longer exists (e.g. it was deleted — even by another
    // owner while you were in it), fall back to home so you're never stuck
    // pointing at a dead space.
    const applyList = (list) => {
      if (!alive) return;
      const safe = Array.isArray(list) ? list : [];
      const act = activeSpaceId();
      if (act && safe.length && !safe.some((s) => s.spaceId === act)) {
        clearActiveSpace();
        bump((n) => n + 1); // re-render so `active` recomputes to home
      }
      setSpaces(safe);
    };
    spaceApi.listCached().then(applyList);
    // Refetch (fresh) when a space is renamed/created/deleted elsewhere, so the
    // active-workspace name here updates without a page reload.
    const onChanged = () => {
      spaceApi
        .list()
        .then((r) => applyList(r?.data))
        .catch(() => {});
    };
    window.addEventListener(SPACES_CHANGED_EVENT, onChanged);
    return () => {
      alive = false;
      window.removeEventListener(SPACES_CHANGED_EVENT, onChanged);
    };
  }, [userInfo]);

  // Close the dropdown on any outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const activeSpace = spaces.find((s) => s.spaceId === active);
  const name = activeSpace ? activeSpace.name : 'My Workspace';
  const isHome = activeSpace ? !!activeSpace.isHome : true;

  const switchTo = (sid) => {
    setOpen(false);
    if (sid === active) return;
    setActiveSpace(sid);
    window.location.reload(); // let every app pick up the new active space
  };
  const manage = () => {
    setOpen(false);
    history.push('/workspaces');
  };

  const menu = open && (
    <div className={`hive-wsmenu hive-wsmenu-${variant}`} role="menu">
      <div className="hive-wsmenu-label">Switch workspace</div>
      {spaces.map((s) => (
        <button
          key={s.spaceId}
          type="button"
          role="menuitem"
          className={`hive-wsmenu-item${s.spaceId === active ? ' is-active' : ''}`}
          onClick={() => switchTo(s.spaceId)}
        >
          <Icon name={s.isHome ? 'Home' : 'Users'} size={15} />
          <span className="hive-wsmenu-item-name">{s.name}</span>
          {s.spaceId === active && <Icon name="Check" size={15} className="hive-wsmenu-check" />}
        </button>
      ))}
      <button type="button" className="hive-wsmenu-foot" onClick={manage}>
        <Icon name="Settings" size={14} /> Manage workspaces
      </button>
    </div>
  );

  if (variant === 'strip') {
    return (
      <div className="hive-wsmenu-wrap" ref={wrapRef}>
        <button
          type="button"
          className={`hive-wsstrip hive-tap${isHome ? '' : ' is-shared'}`}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <Icon name={isHome ? 'Home' : 'Users'} size={15} />
          <span className="hive-wsstrip-eyebrow">Workspace</span>
          <span className="hive-wsstrip-name">{name || '…'}</span>
          <Icon name="ChevronDown" size={15} className="hive-wsstrip-chev" />
        </button>
        {menu}
      </div>
    );
  }

  return (
    <div className="hive-wsmenu-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`hive-wspill hive-tap${isHome ? '' : ' is-shared'}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="hive-wspill-ic">
          <Icon name={isHome ? 'Home' : 'Users'} size={16} color="#fff" />
        </span>
        <span className="hive-wspill-body">
          <span className="hive-wspill-eyebrow">Workspace</span>
          <span className="hive-wspill-name">{name || '…'}</span>
        </span>
        <Icon name="ChevronsUpDown" size={16} className="hive-wspill-chev" />
      </button>
      {menu}
    </div>
  );
}
